import { isBase64 } from '@sie/web-common-utils';

var isSmallestSize = function isSmallestSize(width) {
  return width <= 54;
};
/**
 * Below is a list of supported widths. Note that if the source image is less than
 * the requested width the original source image size is returned
 * @see {@link https://confluence.sie.sony.com/display/KCEO/PXG+Integration+with+Image+Manager}
 */


var widths = [5000, 2048, 1920, 1024, 940, 780, 620, 440, 230, 180, 94, 54];
/**
 * Ensures the standard widths are used when making requests to image manager
 * @param {number} width the target width of the image
 * @returns {number} the width to request from image manager
 */

var findNearestWidth = function findNearestWidth(width) {
  var index = widths.findIndex(function (w) {
    return width > w;
  });
  var nearestWidth = index === 0 ? widths[0] : widths[index - 1];
  return nearestWidth || widths[widths.length - 1];
};
/**
 * Returns the line specific image manager origin
 * @param {string} npEnvironment possible values [np, e1-np]
 * @returns {string}
 */


var imageManagerOriginFor = function imageManagerOriginFor(npEnvironment) {
  return 'https://image.{envExceptNp}.api.playstation.com'.replace('{envExceptNp}.', npEnvironment === 'np' ? '' : "".concat(npEnvironment, "."));
};
/**
 * Description of which source urls to rewrite to use image manager and the details
 * of how to locate the proper location of the image.
 * @see {@link https://confluence.sie.sony.com/display/KCEO/PXG+Integration+with+Image+Manager}
 */


var imageManagerRedirectRules = {
  // store promo
  'snesjc.download.akamai.com': {
    whitelist: ['nsx']
  },
  // Verified user post, Homer
  'homer.download.akamai.com': {
    whitelist: ['vut', 'pr']
  },
  // https://image.api.playstation.com/pr/bam-art/100/210/9559e7de-244e-4172-906c-d01f16d867b9.jpg
  // https://homer.dl.playstation.net/pr/bam-art/100/210/9559e7de-244e-4172-906c-d01f16d867b9.jpg
  'homer.dl.playstation.net': {
    whitelist: ['vut', 'pr']
  },
  // Trophy Image, user Avatar, CDN content
  // https://sce.download.akamai.com/trophy/np/NPWR18513_00_0030F9119D2E54FB7CD1210E8965AD8CB869EAD98C/61449862C06F1B366186B5121A25344A0CD37B32.PNG?w=180&thumb=false
  // https://image.api.playstation.com/trophy/np/NPWR18513_00_0030F9119D2E54FB7CD1210E8965AD8CB869EAD98C/61449862C06F1B366186B5121A25344A0CD37B32.PNG?w=180&thumb=false
  'sce.download.akamai.com': {
    whitelist: ['trophy', 'avatar', 'cdn']
  },
  // https://trophy01.np.community.playstation.net/trophy/np/NPWR16646_00_00A5B72F0AFE074EB8E919219DF65AC0742A3FE260/CBF6CD6842EECE7811EA3BC7F710DAA80C97009D.PNG
  // https://image.api.playstation.com/trophy/np/NPWR16646_00_00A5B72F0AFE074EB8E919219DF65AC0742A3FE260/CBF6CD6842EECE7811EA3BC7F710DAA80C97009D.PNG
  'trophy01.np.community.playstation.net': {
    whitelist: ['trophy']
  },
  // Profile
  'kmj-profile.sftp.upload.akamai.com': {
    whitelist: ['profile']
  },
  // Spotify
  'kmj-music.download.akamai.com': {
    whitelist: ['spotify'],
    srcPath: '/886594'
  },
  // Homer e1',
  'homer.dev.dl.playstation.net': {
    whitelist: ['pr/e1']
  },
  // Apollo feed images
  // https://image.api.playstation.com/cdn/video/ALL/UV0008/NPVB51426_CN/0000000000000000000000000g077r.JPG?w=230&thumb=false
  // https://apollo.dl.playstation.net/cdn/video/ALL/UV0008/NPVB51426_CN/0000000000000000000000000g077r.JPG?w=230&thumb=false
  'apollo.dl.playstation.net': {
    whitelist: ['cdn']
  },
  // Apollo e1 dev
  'apollo2.dev.dl.playstation.net': {
    whitelist: ['cdn/dev']
  },
  // Apollo e1
  // example https://image.e1-np.api.playstation.com/cdn/e1/UP9000/EXDG10014_00/qNHYwJZgLUROtvRzStoMtaSnNilgjaNN.png?w=100
  // https://apollo2.e1-np.ac.playstation.net/UP9000/EXDG10014_00/qNHYwJZgLUROtvRzStoMtaSnNilgjaNN.png?w=100
  'apollo.e1-np.ac.playstation.net': {
    whitelist: ['cdn/e1']
  },
  'apollo2.e1-np.ac.playstation.net': {
    whitelist: ['cdn/e1']
  },
  //
  // 'apollo.e1-np.ac.playstation.net': ['cdn/e1'],
  //
  'apollo2.dl.playstation.net': {
    whitelist: ['cdn/e1']
  },
  // Themis',
  'themis.dl.playstation.net': {
    whitelist: ['themis']
  },
  // Vulcan
  // example https://image.api.playstation.com/vulcan/img/cfn/11307tE57uFadoEQpgcuXpLvEXYNMQ_OnLoyvfzjKfkuLrAIACe45oyT19MqKIDFplj6oldcTjC1EPiSS5aTVn_CWhMZinwg.png&sign=2fb5b6a6ee4faa784d5fd560bd6c4d06d67c1108&w=1024&h=1024
  // https://vulcan.dl.playstation.net/img/cfn/11307tE57uFadoEQpgcuXpLvEXYNMQ_OnLoyvfzjKfkuLrAIACe45oyT19MqKIDFplj6oldcTjC1EPiSS5aTVn_CWhMZinwg.png&sign=2fb5b6a6ee4faa784d5fd560bd6c4d06d67c1108&w=1024&h=1024
  'vulcan.dl.playstation.net': {
    whitelist: ['vulcan']
  },
  // Vulcan e1
  // example https://image.e1-np.api.playstation.com/vulcan/e1/vep-aws-e1/img/rnd/201910/2219/cZfo5spOgBo5cPfvnz32vy7D.png?circle=1000
  // https://vulcan.dev.dl.playstation.net/vep-aws-e1/img/rnd/201910/2219/cZfo5spOgBo5cPfvnz32vy7D.png?circle=1000
  'vulcan.dev.dl.playstation.net': {
    whitelist: ['vulcan/e1']
  },
  // scp
  'scp-cdn.playstation.net': {
    whitelist: ['scp']
  },
  // Facebook
  'graph.facebook.com': {
    whitelist: ['graph']
  },
  // Satchel
  'dpqkifryad8yh.cloudfront.net': {
    whitelist: ['satchel']
  },
  // Gracenote
  'spe1.tmsimg.com': {
    whitelist: ['tmsimg']
  },
  // pxg-08 static images e1
  // https://image.e1-np.api.playstation.com/static/e1-np/public/images/0.1.1/default-game-230x230.png
  // https://static.e1-np.playstation.com/images/0.1.1/default-game-230x230.png
  'static.e1-np.playstation.com': {
    whitelist: ['images'],
    imageManagerPath: '/static/e1-np/public'
  },
  'static.playstation.com': {
    whitelist: ['images'],
    imageManagerPath: '/static/prod/public'
  }
};
/**
 * Whether or not the given host is the image manager service
 * @param {string} host host to test
 * @returns {boolean}
 */

var isImageManagerHost = function isImageManagerHost(host) {
  return /^image\.([\w-]+\.)?api\.playstation\.com$/.test(host);
};
/**
 * Whether or not the given host is the legacy kamaji image service
 * @param {string} host host to test
 * @returns {boolean}
 */


var isKamajiImageServiceHost = function isKamajiImageServiceHost(host) {
  return /^image\.api\.([\w-]+)\.km\.playstation\.net$/.test(host);
};

var getImageSrcUrl = function getImageSrcUrl(url) {
  if (!isKamajiImageServiceHost(url.host)) return url;
  return new URL(url.searchParams.get('image'));
};

var isImageManagerEnabled = function isImageManagerEnabled(_ref) {
  var pathname = _ref.pathname,
      whitelist = _ref.whitelist;
  return !!whitelist.find(function (source) {
    return pathname.includes("/".concat(source, "/"));
  });
};

var swapInImageManager = function swapInImageManager(url) {
  var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref2$npEnvironment = _ref2.npEnvironment,
      npEnvironment = _ref2$npEnvironment === void 0 ? 'e1-np' : _ref2$npEnvironment;

  var imageUrl = new URL(url);
  var redirectRules = imageManagerRedirectRules[imageUrl.host];

  if (!redirectRules || !isImageManagerEnabled({
    pathname: imageUrl.pathname,
    whitelist: redirectRules.whitelist
  })) {
    return false;
  }

  imageUrl.href = imageUrl.href.replace("".concat(imageUrl.origin).concat(redirectRules.srcPath || ''), "".concat(imageManagerOriginFor(npEnvironment)).concat(redirectRules.imageManagerPath || ''));
  return imageUrl;
};

var formatImageManagerUrl = function formatImageManagerUrl(url) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var width = options.width,
      thumb = options.thumb;
  var imageUrl = isImageManagerHost(url.host) ? url : swapInImageManager(url.toString(), options);
  if (!imageUrl) return false;
  if (width) imageUrl.searchParams.set('w', findNearestWidth(width));
  if (thumb != null) imageUrl.searchParams.set('thumb', thumb);
  return imageUrl.toString();
};
/**
 * Resolves the source url to use the image manager service unless the source
 * url is not known to have support with image manager
 *
 * @param {string} url The image url source to get the parsed url from.
 * @param {Object} options The options to pass to image manager.
 * @param {number} options.width The width of the image to request.
 * @param {boolean} options.thumb Make the request for a low quality image.
 * @returns {string} The final image url to use
 */


var getImageUrl = function getImageUrl(url) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var imageUrl;

  try {
    imageUrl = getImageSrcUrl(new URL(url));
  } catch (e) {
    return {
      originalSrc: url,
      previewSrc: null,
      src: null,
      srcset: null
    };
  }

  var imageManagerUrl = formatImageManagerUrl(imageUrl, options);
  var usesImageManager = !!imageManagerUrl;

  var makeSrcSet = function makeSrcSet() {
    var scales = [1, 1.5, 2];
    if (typeof options.width !== 'number') return null;
    return scales.map(function (scale) {
      var scaledSrc = formatImageManagerUrl(imageUrl, {
        npEnvironment: options.npEnvironment,
        width: options.width * scale,
        thumb: false
      });
      return "".concat(scaledSrc).concat(scale === 1 ? '' : " ".concat(scale, "x"));
    }).join(', ');
  };

  var showBlurredPreview = !isBase64(url) && usesImageManager && !isSmallestSize(options.width);
  return {
    originalSrc: url,
    previewSrc: showBlurredPreview ? formatImageManagerUrl(imageUrl, {
      width: 54,
      thumb: true,
      npEnvironment: options.npEnvironment
    }) : null,
    src: imageManagerUrl || imageUrl.toString(),
    srcset: usesImageManager ? makeSrcSet() : null
  };
};

export default getImageUrl;